import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Music,
  User,
  LogOut,
  Settings,
  Plus,
  ChevronDown,
  Sparkles,
  Crown,
  Briefcase,
  Wrench
} from "lucide-react";
import { logoutUser } from "../../redux/reduxSlices/authSlice";
import { useDispatch } from "react-redux";
import brand_logo from "../../assets/brand_logo.webp"

const roleMeta = {
  "music-creator": {
    label: "Music Creator",
    icon: Music,
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-gradient-to-r from-purple-500 to-pink-500"
  },
  "sales-person": {
    label: "Sales Person",
    icon: Briefcase,
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-gradient-to-r from-blue-500 to-cyan-500"
  },
  "admin": {
    label: "Admin",
    icon: Wrench,
    color: "from-green-500 to-emerald-500",
    bgColor: "bg-gradient-to-r from-green-500 to-emerald-500"
  },
  "owner": {
    label: "Owner",
    icon: Crown,
    color: "from-amber-500 to-orange-500",
    bgColor: "bg-gradient-to-r from-amber-500 to-orange-500"
  },
};

const Header = ({ user, onNewOrder, onProfile }) => {
  const meta = roleMeta[user?.role] || {
    label: "User",
    icon: User,
    color: "from-gray-500 to-gray-600",
    bgColor: "bg-gradient-to-r from-gray-500 to-gray-600"
  };
  const RoleIcon = meta.icon;
  const displayName = user?.first_name || user?.username || user?.email || "User";
  const canCreateOrder = user?.role === "sales-person" && typeof onNewOrder === "function";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch()

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const onLogout = () => {
    dispatch(logoutUser())
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="mx-auto px-4 ">
        <div className="flex items-center justify-between">
          {/* Left: Logo + Brand */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(`${user.user_type == "music_creator" ? "/music-creator" : "/sales-person"}`)}>
              <img className="w-48 h-16 object-contain" src={brand_logo} alt="Brand Logo" />
            </div>

            {/* Role Badge - Compact */}
            <div className={`px-2 py-1 rounded ${meta.bgColor} text-white text-xs font-medium flex items-center gap-1`}>
              <RoleIcon className="w-3 h-3" />
              <span>{meta.label}</span>
            </div>
          </div>

          {/* Right: Actions & User Menu */}
          <div className="flex items-center gap-3">
            {/* Welcome Text - Compact */}
            <div className="hidden sm:block text-right mr-1">
              <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">Hi, {displayName}</div>
            </div>

            {/* New Order Button - Compact */}
            {canCreateOrder && (
              <button
                className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:shadow-md transition-shadow"
                onClick={onNewOrder}
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Order</span>
              </button>
            )}

            {/* User Menu - Compact */}
            <div className="relative" ref={menuRef}>
              <button
                className="flex items-center gap-1.5 p-1 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-white" />
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu - Compact */}
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                  {/* User Info - Compact */}
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="text-sm font-medium text-gray-900 truncate">{displayName}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${meta.bgColor}`}></div>
                      <div className="text-xs text-gray-500">{meta.label}</div>
                    </div>
                  </div>

                  {/* Menu Items - Compact */}
                  <div className="py-1">
                    <button
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        setMenuOpen(false);
                        if (typeof onProfile === 'function') onProfile();
                        navigate('/profile');
                      }}
                    >
                      <Settings className="w-3.5 h-3.5 text-gray-400" />
                      <span>Profile</span>
                    </button>

                    <button
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setMenuOpen(false);
                        if (typeof onLogout === 'function') onLogout();
                      }}
                    >
                      <LogOut className="w-3.5 h-3.5 text-red-400" />
                      <span>Logout</span>
                    </button>
                  </div>

                  {/* Footer - Compact */}
                  <div className="px-3 py-1.5 border-t border-gray-100">
                    <div className="text-[10px] text-gray-400 text-center">
                      Digikore Jingle v1.0
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;